'use es6';

import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import throttle from 'transmute/throttle';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ON_SCROLL_THROTTLE = 100;

// https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry/contentBoxSize#value
const getHeightFromResizeObserverEntries = entries => {
  try {
    return entries[0].contentBoxSize[0].blockSize || 0;
  } catch (e) {
    return 0;
  }
};
class ScrollWrapper extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      inTop: true,
      inBottom: true,
      height: 0,
      distanceToBottom: 0,
      shouldKeepAtBottom: true,
      previousContentHeight: 0
    };
    this.scrollContainer = /*#__PURE__*/createRef();
    this.scrollContent = /*#__PURE__*/createRef();
    const resizeCallback = entries => {
      const newContentHeight = getHeightFromResizeObserverEntries(entries);
      const contentGrew = this.state.previousContentHeight < newContentHeight;
      if (contentGrew && this.inThreshold().inTop) {
        this.props.onScrollToTop();
      }
      this.setState({
        previousContentHeight: newContentHeight
      });
    };
    resizeCallback.bind(this);
    this.contentResizeObserver = new ResizeObserver(resizeCallback);
    this.onResize = throttle(ON_SCROLL_THROTTLE, this.onResize.bind(this));
    this.onScroll = throttle(ON_SCROLL_THROTTLE, this.onScroll.bind(this));
  }
  componentDidMount() {
    const {
      current
    } = this.scrollContent;
    if (current) {
      this.setState({
        height: current.clientHeight
      });
      this.scrollToBottom();
      this.contentResizeObserver.observe(current);
    }
    window.addEventListener('resize', this.onResize, {
      passive: true
    });
  }
  getSnapshotBeforeUpdate() {
    const {
      current
    } = this.scrollContainer;
    if (!current) return null;
    return {
      prevScrollTop: this.getScrollDistanceFromTop()
    };
  }
  componentDidUpdate(__prevProps, prevState, snapshot) {
    const {
      current
    } = this.scrollContainer;
    if (!snapshot || !current) return;
    if (this.state.shouldKeepAtBottom) {
      this.scrollToBottom();
      return;
    }
    if (this.state.distanceToBottom === 0) {
      this.setState({
        shouldKeepAtBottom: true
      });
      return;
    }
    const {
      prevScrollTop
    } = snapshot;
    const currentScrollTop = this.getScrollDistanceFromTop();
    if (currentScrollTop !== prevScrollTop) return;
    if (prevState.height > this.state.height) {
      this.setScrollTop(currentScrollTop + (prevState.height - this.state.height));
    }
  }
  componentWillUnmount() {
    this.contentResizeObserver.disconnect();
    window.removeEventListener('resize', this.onResize);
  }
  getScrollDistanceFromTop() {
    const {
      current: scrollWrapper
    } = this.scrollContainer;
    const {
      current: content
    } = this.scrollContent;
    const heightDiff = scrollWrapper.scrollHeight - content.clientHeight;
    return scrollWrapper.scrollTop - heightDiff;
  }
  onScroll() {
    const {
      onScrollToTop,
      onScrollFromBottom,
      onScrollToBottom
    } = this.props;
    if (!this.scrollContainer.current || !this.scrollContent.current) {
      return;
    }
    const {
      inTop,
      inBottom
    } = this.inThreshold();
    if (!this.state.inTop && inTop) {
      onScrollToTop();
    }
    if (!this.state.inBottom && inBottom) {
      onScrollToBottom();
    }
    if (this.state.inBottom && !inBottom) {
      onScrollFromBottom();
    }
    const distanceToBottom = this.getScrollDistanceFromBottom();
    this.setState({
      inTop,
      inBottom,
      distanceToBottom,
      shouldKeepAtBottom: false
    });
  }
  inThreshold() {
    const {
      topThreshold,
      bottomThreshold
    } = this.props;
    const distanceFromTop = this.getScrollDistanceFromTop();
    const distanceFromBottom = this.getScrollDistanceFromBottom();
    return {
      inTop: distanceFromTop <= topThreshold,
      inBottom: distanceFromBottom <= bottomThreshold
    };
  }
  getScrollDistanceFromBottom() {
    const {
      current
    } = this.scrollContainer;
    if (!current) {
      return 0;
    }
    const {
      scrollTop,
      scrollHeight: fullContentHeight,
      clientHeight,
      offsetHeight
    } = current;
    const containerHeight = clientHeight || offsetHeight;
    return fullContentHeight - containerHeight - scrollTop;
  }
  scrollToBottom() {
    const {
      current
    } = this.scrollContainer;
    if (current) {
      this.setScrollTop(current.scrollHeight);
    }
    this.setState({
      shouldKeepAtBottom: true
    });
  }
  setScrollTop(scrollTop) {
    const {
      current
    } = this.scrollContainer;
    if (current) {
      current.scrollTop = scrollTop || 0;
    }
  }
  onResize() {
    const {
      current
    } = this.scrollContent;
    if (current) {
      this.setState({
        height: current.clientHeight || 0
      });
    }
  }
  render() {
    const {
      children,
      className,
      stickToTop
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      className: className,
      style: {
        flexGrow: 1
      },
      onScroll: this.onScroll,
      ref: this.scrollContainer,
      "data-test-id": "scroll-container",
      children: [stickToTop, /*#__PURE__*/_jsx("div", {
        ref: this.scrollContent,
        children: children
      })]
    });
  }
}
ScrollWrapper.displayName = 'ScrollWrapper';
ScrollWrapper.propTypes = {
  bottomThreshold: PropTypes.number.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  onScrollFromBottom: PropTypes.func.isRequired,
  onScrollToBottom: PropTypes.func.isRequired,
  onScrollToTop: PropTypes.func.isRequired,
  stickToTop: PropTypes.node,
  topThreshold: PropTypes.number.isRequired
};
const noop = () => {};
ScrollWrapper.defaultProps = {
  bottomThreshold: 300,
  onScrollFromBottom: noop,
  onScrollToBottom: noop,
  onScrollToTop: noop,
  topThreshold: 300
};
export default ScrollWrapper;